import { RefObject, useCallback, useEffect, useRef } from 'react';

import { pxToRem } from '@utils/converters';

type UseAutoResizeTextareaOptions = {
  containerRef: RefObject<HTMLElement>;
  inputRowRef: RefObject<HTMLElement>;
  buttonsRowRef: RefObject<HTMLElement>;
  inputValue: string;
  minHeight?: number;
  maxHeightFallback?: number;
  onInputRowHeightChange?: (height: number) => void;
};

export const useAutoResizeTextarea = ({
  containerRef,
  inputRowRef,
  buttonsRowRef,
  inputValue,
  minHeight = 80,
  maxHeightFallback = 240,
  onInputRowHeightChange,
}: UseAutoResizeTextareaOptions) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const resizeTextarea = useCallback(() => {
    const el = textAreaRef.current;
    if (!el) return;

    // Reset height to auto to get the natural scroll height
    el.style.height = 'auto';

    const row = inputRowRef.current;
    const buttons = buttonsRowRef.current;
    const container = containerRef.current;

    // Calculate maximum height based on available container space
    let maxHeight = maxHeightFallback;

    if (container && row) {
      const textRow = row.querySelector<HTMLElement>('[data-input-row="true"]') ?? row;
      const containerStyles = window.getComputedStyle(container);
      const cPadTop = parseFloat(containerStyles.paddingTop || '0');
      const cPadBottom = parseFloat(containerStyles.paddingBottom || '0');
      const innerAvailable = container.clientHeight - cPadTop - cPadBottom;

      const rowStyles = window.getComputedStyle(textRow);
      const paddingTop = parseFloat(rowStyles.paddingTop || '0');
      const paddingBottom = parseFloat(rowStyles.paddingBottom || '0');
      const borderTop = parseFloat(rowStyles.borderTopWidth || '0');
      const borderBottom = parseFloat(rowStyles.borderBottomWidth || '0');
      const gap = parseFloat(rowStyles.rowGap || rowStyles.gap || '0');
      const buttonsHeight = buttons?.offsetHeight ?? 0;

      maxHeight = Math.max(
        minHeight,
        Math.floor(
          innerAvailable -
            paddingTop -
            paddingBottom -
            borderTop -
            borderBottom -
            gap -
            buttonsHeight -
            24 // account for the height of the textarea itself
        )
      );
    }

    // Set the new height within bounds
    const newHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = pxToRem(newHeight);
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';

    // Notify parent component about input row height change
    if (row && onInputRowHeightChange) {
      onInputRowHeightChange(row.offsetHeight);
    }
  }, [
    containerRef,
    inputRowRef,
    buttonsRowRef,
    minHeight,
    maxHeightFallback,
    onInputRowHeightChange,
  ]);

  useEffect(() => {
    resizeTextarea();
  }, [inputValue, resizeTextarea]);

  useEffect(() => {
    const handleWindowResize = () => resizeTextarea();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [resizeTextarea]);

  return {
    textAreaRef,
    resizeTextarea,
  };
};
